/**
 * PipelineFilters (SPEC E3 — pipeline-and-capi, amendment E3-M1).
 *
 * Filter bar for the kanban: by owner/assignee, created-date range, and value
 * range. The parent persists the active filters in URL params for bookmarking.
 * Filtering is applied client-side over the per-column opportunity lists.
 */

import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import type { KanbanFilters, Opportunity } from '@/types/pipeline';

interface PipelineFiltersProps {
  filters: KanbanFilters;
  onChange: (next: KanbanFilters) => void;
}

export function PipelineFilters({ filters, onChange }: PipelineFiltersProps) {
  const set = (patch: Partial<KanbanFilters>) =>
    onChange({ ...filters, ...patch });

  const clear = () => onChange({});

  const hasAny =
    !!filters.owner ||
    !!filters.createdAfter ||
    !!filters.createdBefore ||
    filters.minValue !== undefined ||
    filters.maxValue !== undefined;

  return (
    <div
      className="flex flex-wrap items-end gap-3 px-1 py-2"
      data-testid="pipeline-filters"
    >
      <div className="space-y-1">
        <Label htmlFor="filter-owner" className="text-xs">
          Owner
        </Label>
        <Input
          id="filter-owner"
          className="h-8 w-40"
          placeholder="owner / assignee"
          value={filters.owner || ''}
          onChange={(e) => set({ owner: e.target.value || undefined })}
        />
      </div>
      <div className="space-y-1">
        <Label htmlFor="filter-after" className="text-xs">
          Created after
        </Label>
        <Input
          id="filter-after"
          type="date"
          className="h-8 w-40"
          value={filters.createdAfter || ''}
          onChange={(e) => set({ createdAfter: e.target.value || undefined })}
        />
      </div>
      <div className="space-y-1">
        <Label htmlFor="filter-before" className="text-xs">
          Created before
        </Label>
        <Input
          id="filter-before"
          type="date"
          className="h-8 w-40"
          value={filters.createdBefore || ''}
          onChange={(e) => set({ createdBefore: e.target.value || undefined })}
        />
      </div>
      <div className="space-y-1">
        <Label htmlFor="filter-min" className="text-xs">
          Min value
        </Label>
        <Input
          id="filter-min"
          type="number"
          className="h-8 w-28"
          value={filters.minValue ?? ''}
          onChange={(e) =>
            set({
              minValue: e.target.value === '' ? undefined : Number(e.target.value),
            })
          }
        />
      </div>
      <div className="space-y-1">
        <Label htmlFor="filter-max" className="text-xs">
          Max value
        </Label>
        <Input
          id="filter-max"
          type="number"
          className="h-8 w-28"
          value={filters.maxValue ?? ''}
          onChange={(e) =>
            set({
              maxValue: e.target.value === '' ? undefined : Number(e.target.value),
            })
          }
        />
      </div>
      {hasAny && (
        <Button variant="ghost" size="sm" className="h-8" onClick={clear}>
          Clear
        </Button>
      )}
    </div>
  );
}

/** Apply the kanban filters to an opportunity list (client-side). */
export function applyFilters(
  opps: Opportunity[],
  filters: KanbanFilters
): Opportunity[] {
  return opps.filter((opp) => {
    if (filters.owner) {
      const owner = String(
        opp.owner || opp.assignee || opp.setter || ''
      ).toLowerCase();
      if (!owner.includes(filters.owner.toLowerCase())) return false;
    }
    if (filters.createdAfter && opp.created_at) {
      if (new Date(opp.created_at) < new Date(filters.createdAfter)) return false;
    }
    if (filters.createdBefore && opp.created_at) {
      if (new Date(opp.created_at) > new Date(filters.createdBefore)) return false;
    }
    if (filters.minValue !== undefined) {
      if ((opp.value ?? -Infinity) < filters.minValue) return false;
    }
    if (filters.maxValue !== undefined) {
      if ((opp.value ?? Infinity) > filters.maxValue) return false;
    }
    return true;
  });
}
